// ═══════════════════════════════════════════════════
//  Client-side Banquette Cost Calculation
//  Mirrors server-side helpers for live cost preview
// ═══════════════════════════════════════════════════

import {
  type MaterialLibrary,
  type BanquetteConfig,
  type FabricPackingResult,
  type FabricPatternDirection,
  type Piece,
  type ProductInputs,
  type SlotInputs,
  generateFabricPieces,
  packFabricPieces,
  computeDesignBearingFraction,
  resolveDesignTypeAllowancePct,
  resolveDesignTraits,
  resolveWireLinearFeet,
  resolveChannelCount,
  resolveChannelWidthWarning,
  resolveButtonCount,
  resolveSpringCount,
  resolveButtonHardware,
  resolveWireHardware,
  type ComponentNestingPart,
  collectFinishablePanels,
  computeWoodFinish,
  type FinishRate,
  type WoodFinishRow,
  type Finish,
  type UpholsteryDesignAllowances,
  type DesignTraitSource,
  componentDims,
  resolveFramingDims,
  resolveFoamLayers,
  priceMaterialByUnit,
  type MaterialUnitPrice,
  type ChargedUnitLabel,
  type UnitPricedPiece,
} from '@estimator/shared';

/** Project-level context for banquette fabric (client preview). Currently unused but kept for future expansion. */
export interface BanquetteCostPreviewContext {}

function coercePatternDirection(raw: unknown): FabricPatternDirection {
  if (raw === 'railroad' || raw === 'up_the_bolt') return raw;
  return 'none';
}

export interface BanquetteMaterialDetail {
  material_id: string;
  material_name: string;
  total_sqft: number;
  sqft_with_waste: number;
  cost: number;
  /** What was billed and in which unit, for the breakdown label. */
  charged_qty?: number;
  charged_unit?: ChargedUnitLabel;
  num_sheets?: number;
  sheet_size?: string;
}

export interface BanquetteSlotDetail {
  /** Identity field used to match slots during per-material fabric cost apportionment. */
  slot_id: string;
  name: string;
  calc_mode: string;
  real_inches: number;
  sqft: number;
  cost: number;
  material_name?: string;
  num_sheets?: number;
  // Material identity
  material_id?: string;
  material_category?: string;
  material_subtype?: string | null;
  /** Cut pieces produced for this fabric slot (pre-packing). */
  fabric_pieces?: Array<{
    panel_type: 'front' | 'side' | 'back';
    section_index: number;
    panel_index?: number;
    width_in: number;
    length_in: number;
    design_up_axis: 'width' | 'length';
  }>;
  // Non-fabric breakdown intermediate
  waste_factor_used?: number;
  // Display extras
  sheet_size?: string;
  note?: string;
}

export interface BanquetteCostPreviewResult {
  framing_cost: number;
  /** Cost of non-fabric slot materials (laminate, veneer, etc.). */
  finish_cost: number;
  /** Cost of upholstery materials (fabric, vinyl, leather, velvet, etc.). */
  fabric_cost: number;
  foam_cost: number;
  dacron_cost: number;
  hardware_cost: number;
  /** Why a button charge came out at zero, when it did. Empty is normal. */
  hardware_warnings: string[];
  /** Total foam-layer area, excluding dacron. */
  foam_sqft: number;
  dacron_sqft: number;
  foam_materials: BanquetteMaterialDetail[];
  foam_layers: Array<{ name: string; width: number; sqft: number }>;
  spring_count: number;
  clip_count: number;
  connecting_wire_lf: number;
  button_count: number;
  /** Linear feet of tufting wire. Only designs with the is_tufted trait use it. */
  wire_lf: number;
  hardware_items: Array<{
    name: string;
    hardware_id: string;
    qty: number;
    cost_per_unit: number;
    cost: number;
    unit: string;
  }>;
  total_cost: number;
  /** total_cost minus fabric_cost. */
  total_cost_excluding_fabric: number;
  /** Applied wood finish on framing panels — distinct from finish_cost, which
   *  is drawn-slot surface material. */
  wood_finish_cost: number;
  wood_finish_details: WoodFinishRow[];
  rate_per_lf: number;
  framing_materials: BanquetteMaterialDetail[];
  slot_details: BanquetteSlotDetail[];
  rib_count: number;
  /** One entry per fabric material across all slots. Empty array if no fabric slots. */
  fabric_packings: FabricPackingResult[];
  /** Sheet-goods rectangles for the nesting preview. Fabric excluded — it is not nested. */
  nesting_parts: ComponentNestingPart[];
}

function resolveRibCount(
  framing: { rib_mode?: string; rib_spacing?: number; rib_count?: number },
  lfQty: number,
): number {
  if (framing.rib_mode === 'manual') return Math.max(0, Math.floor(framing.rib_count ?? 0));
  const spacing = framing.rib_spacing ?? 24;
  if (spacing <= 0) return 0;
  return Math.max(0, Math.floor((lfQty * 12) / spacing) - 1);
}

/** Options a caller can add to computeSheetCost beyond area and waste. */
interface ComputeSheetCostOptions {
  /**
   * How many discrete pieces this material is cut into. Only an `each`
   * material needs it — area carries no piece count — but passing it is
   * harmless for every other unit.
   */
  pieceQty?: number;
  /**
   * The rectangles cut from this material. Only linear-foot pricing reads
   * them, to rip whole boards instead of dividing a pooled area by the board
   * width. Omitting them falls back to the area model.
   */
  pieces?: readonly UnitPricedPiece[];
  /**
   * Sink for "could not price this" warnings. Pass the same array the caller
   * folds into `hardware_warnings`; duplicates are dropped, since one
   * misconfigured material is usually priced several times in a run.
   */
  warnings?: string[];
  /**
   * Filled in with the full pricing result. Lets the caller label a breakdown
   * row with the quantity actually billed — `4.4 lf` rather than the square
   * footage every row used to print regardless of unit.
   */
  result?: { price?: MaterialUnitPrice };
}

/**
 * Numeric mirror of the server's computeSheetCost. The unit semantics live in
 * `priceMaterialByUnit` in @estimator/shared, so this side and the server side
 * cannot drift the way they did while each compared `material.unit` against
 * its own `'sq ft'` / `'lin ft'` literals — literals that matched neither of
 * the `square_foot` / `linear_foot` spellings the Materials Library writes.
 *
 * Note the extra `convertedCostPerUnit` in position 4, which the server
 * signature does not have: this side prices in the display currency.
 */
function computeSheetCost(
  totalSqft: number,
  material: MaterialLibrary | null,
  wasteFactor: number,
  convertedCostPerUnit?: number,
  costMode: 'whole_sheet' | 'used_area' = 'whole_sheet',
  opts: ComputeSheetCostOptions = {},
): [number, number, number] {
  if (!material) return [0, 0, 0];

  const priced = priceMaterialByUnit(
    totalSqft,
    { ...material, cost_per_unit: convertedCostPerUnit ?? material.cost_per_unit ?? 0 },
    wasteFactor,
    { costMode, pieceQty: opts.pieceQty, pieces: opts.pieces, materialName: material.name },
  );

  if (opts.warnings) {
    for (const w of priced.warnings) {
      if (!opts.warnings.includes(w)) opts.warnings.push(w);
    }
  }

  if (opts.result) opts.result.price = priced;

  return [priced.num_sheets, priced.cost, priced.sheet_sqft];
}

/**
 * Calculate banquette cost preview from config and materials list.
 * Runs entirely client-side for live updates.
 */
export function calcBanquetteCostPreview(
  config: Partial<BanquetteConfig>,
  materials: MaterialLibrary[],
  pageScalePpi?: number,
  projectCtx?: BanquetteCostPreviewContext,
  opts?: {
    forceCostMode?: 'used_area';
    finishes?: Finish[];
    /** Catalog rows, for their behavior traits. Optional: without them
     *  resolveDesignTraits falls back to the seeded map, which reproduces
     *  this function's previous literal comparisons for the eight shipped
     *  designs. */
    designCatalog?: readonly DesignTraitSource[];
  },
  // Trail opts rather than preceding it as on the back cushion: opts is
  // already positional here, and both existing callers pass it.
  upholsteryAllowances?: UpholsteryDesignAllowances | null,
  hardwareMap?: Map<string, { cost_per_unit: number; name: string; unit?: string }>,
  /** Button hardware to use when the design names none. Mirrors the server
   *  helper's parameter of the same name. */
  defaultButtonHardwareId?: string | null,
  /** Tufting wire hardware to use when the design names none. Mirrors the
   *  parameter of the same name on the other seven helpers. See spec D4. */
  defaultWireHardwareId?: string | null,
): BanquetteCostPreviewResult {
  const matMap = new Map(materials.map((m) => [m.id, m]));

  const lfQty = config.lf_quantity ?? 0;
  const profileDepth = config.profile_depth ?? 18;
  const profileHeight = config.profile_height ?? 20;
  const sectionCount = Math.max(1, Math.floor(config.section_count ?? 1));
  const design = (config as any).design ?? { type: 'plain' };
  /** The base's run axis: vertical channels divide it, horizontal ones divide the height. */
  const sectionWidthInches = sectionCount > 0 ? (lfQty * 12) / sectionCount : 0;
  const framing = resolveFramingDims(config.framing ?? { rails: [], additional_panels: [], waste_factor: 0.1, curved_section_count: 0, curved_section_waste_factor: 0, rib_mode: 'auto' as const, rib_spacing: 24, rib_count: 0, side_panel_material_id: null, rib_material_id: null }, componentDims(config));
  const baseWasteFactor = framing.waste_factor ?? 0.1;
  const curvedR = Math.max(0, Math.floor(framing.curved_section_count ?? 0));
  const curvedExtra = Math.max(0, framing.curved_section_waste_factor ?? 0);
  const curvedFraction = sectionCount > 0 ? Math.min(1, curvedR / sectionCount) : 0;
  const effectiveWasteFactor = baseWasteFactor + curvedExtra * curvedFraction;

  // Aggregate framing by material
  /**
   * "Could not price this material" warnings, e.g. a linear-foot material with
   * no board width. Collected separately from hardwareWarnings so the
   * documented button -> channel -> wire order survives, then appended.
   */
  const materialWarnings: string[] = [];
  const framingByMat = new Map<
    string,
    /**
     * totalQty counts discrete pieces; only `each` pricing reads it.
     * runPieces holds the rectangles themselves, which only linear-foot
     * pricing reads, to rip whole boards instead of dividing a pooled area.
     */
    { mat: MaterialLibrary; totalSqft: number; totalQty: number; runPieces: UnitPricedPiece[] }
  >();

  const nestingParts: ComponentNestingPart[] = [];

  /**
   * Record one rectangle. Area feeds the cost roll-up; the geometry feeds the
   * nesting preview. canRotate mirrors the server part-collector.
   */
  function addPiece(
    name: string,
    widthIn: number,
    heightIn: number,
    qty: number,
    materialId: string | null | undefined,
    canRotate: boolean,
    /** Run a linear-foot board along the piece's shorter dimension. */
    boardRunFlipped = false,
  ) {
    if (!materialId || widthIn <= 0 || heightIn <= 0 || qty <= 0) return;
    const mat = matMap.get(materialId);
    if (!mat) return;
    const sqft = (widthIn * heightIn * qty) / 144;
    const runPiece: UnitPricedPiece = {
      width_in: widthIn, length_in: heightIn, qty, flipped: boardRunFlipped,
    };
    const existing = framingByMat.get(materialId);
    if (existing) {
      existing.totalSqft += sqft;
      existing.totalQty += qty;
      existing.runPieces.push(runPiece);
    } else {
      framingByMat.set(materialId, { mat, totalSqft: sqft, totalQty: qty, runPieces: [runPiece] });
    }
    nestingParts.push({
      name,
      width_in: widthIn,
      height_in: heightIn,
      qty,
      material_id: materialId,
      waste_factor: effectiveWasteFactor,
      can_rotate: canRotate,
    });
  }

  // Side panels
  addPiece('Side Panel', profileDepth, profileHeight, 2 * sectionCount, framing.side_panel_material_id, true);

  // Ribs
  const ribCount = resolveRibCount(framing, lfQty);
  if (ribCount > 0) {
    addPiece('Rib', profileDepth, profileHeight, ribCount, framing.rib_material_id, true);
  }

  // Rails
  for (const rail of framing.rails ?? []) {
    addPiece(rail.name ?? 'Rail', rail.width ?? 0, rail.length ?? 0, 1, rail.material_id, false, rail.board_run_flipped ?? false);
  }

  // Additional panels
  for (const panel of framing.additional_panels ?? []) {
    const totalQty = (panel.qty_per_section ?? 1) * sectionCount;
    addPiece(panel.name ?? 'Panel', panel.width ?? 0, panel.height ?? 0, totalQty, panel.material_id, true, panel.board_run_flipped ?? false);
  }

  // Compute framing cost
  let framingCost = 0;
  const framingMaterials: BanquetteMaterialDetail[] = [];
  for (const [matId, group] of framingByMat) {
    const priceOut: { price?: MaterialUnitPrice } = {};
    const [numSheets, cost] = computeSheetCost(group.totalSqft, group.mat, effectiveWasteFactor, undefined, opts?.forceCostMode ?? 'whole_sheet',
      { pieceQty: group.totalQty, pieces: group.runPieces, warnings: materialWarnings, result: priceOut });
    framingCost += cost;
    framingMaterials.push({
      material_id: matId,
      material_name: group.mat.name,
      total_sqft: group.totalSqft,
      sqft_with_waste: group.totalSqft * (1 + effectiveWasteFactor),
      cost,
      charged_qty: priceOut.price?.charged_qty,
      charged_unit: priceOut.price?.charged_unit,
      num_sheets: numSheets > 0 ? numSheets : undefined,
      sheet_size: group.mat.unit === 'sheet' ? `${group.mat.sheet_width}x${group.mat.sheet_length}` : undefined,
    });
  }

  // Finish cost from slots
  // Two-pass: (1) per-slot piece generation, (2) per-material packing + cost
  // apportionment. Non-fabric slots still cost as before in pass 1.
  let finishCost = 0;
  let fabricCost = 0;
  const slotDetails: BanquetteSlotDetail[] = [];
  const ppi = pageScalePpi ?? config.image_scale_ppi ?? 0;
  const sectionLfIn = sectionCount > 0 ? (lfQty / sectionCount) * 12 : 0;
  const productInputs: ProductInputs = {
    product_type: 'banquette_base',
    section_count: sectionCount,
    section_lf_in: sectionLfIn,
    profile_height_in: profileHeight,
    profile_depth_in: profileDepth,
  };

  const piecesByMaterial = new Map<
    string,
    {
      mat: MaterialLibrary;
      pieces: Piece[];
      slotDetails: BanquetteSlotDetail[];
    }
  >();

  for (const slot of config.slots ?? []) {
    const slotId: string = (slot as any).id ?? '';
    const mat = slot.material_id ? matMap.get(slot.material_id) ?? null : null;
    if (!mat) {
      slotDetails.push({
        slot_id: slotId,
        name: slot.name,
        calc_mode: slot.calc_mode ?? 'linear',
        real_inches: 0,
        sqft: 0,
        cost: 0,
        note: 'no material',
      });
      continue;
    }
    if (ppi <= 0) {
      slotDetails.push({
        slot_id: slotId,
        name: slot.name,
        calc_mode: slot.calc_mode ?? 'linear',
        real_inches: 0,
        sqft: 0,
        cost: 0,
        note: 'scale not set',
        material_id: mat.id,
        material_name: mat.name,
        material_category: (mat as any).category,
        material_subtype: (mat as any).fabric_subtype ?? null,
      });
      continue;
    }

    let totalPx = 0;
    for (const poly of slot.polylines ?? []) totalPx += poly.length_px ?? 0;
    const realInches = totalPx / ppi;

    const isFabric = !!(mat as any).fabric_subtype || ((mat as any).material_type ?? '').toLowerCase() === 'fabric';

    if (isFabric) {
      const slotInputs: SlotInputs = {
        slot_id: slotId,
        slot_name: slot.name,
        panel_role: slot.panel_role ?? 'front',
        polyline_length_in: realInches,
        staple_allowance_in: slot.fabric_staple_allowance ?? 1,
        fabric_wrap_value: slot.fabric_wrap_value ?? null,
      };
      const pieces = generateFabricPieces(slotInputs, productInputs);
      const rawAreaIn2 = pieces.reduce((s, p) => s + p.width_in * p.length_in, 0);

      const slotInfo: BanquetteSlotDetail = {
        slot_id: slotId,
        name: slot.name,
        calc_mode: slot.calc_mode ?? 'linear',
        real_inches: realInches,
        sqft: rawAreaIn2 / 144,
        cost: 0, // apportioned in per-material pass below
        material_name: mat.name,
        material_id: mat.id,
        material_category: (mat as any).category,
        material_subtype: (mat as any).fabric_subtype ?? null,
        fabric_pieces: pieces.map((p) => ({
          panel_type: p.panel_type,
          section_index: p.section_index,
          panel_index: p.panel_index,
          width_in: p.width_in,
          length_in: p.length_in,
          design_up_axis: p.design_up_axis,
        })),
      };
      slotDetails.push(slotInfo);

      let group = piecesByMaterial.get(mat.id);
      if (!group) {
        group = { mat, pieces: [], slotDetails: [] };
        piecesByMaterial.set(mat.id, group);
      }
      group.pieces.push(...pieces);
      group.slotDetails.push(slotInfo);
    } else {
      // Non-fabric: uses base framing waste only — curved-section uplift
      // applies to framing panels, not finish slots.
      //
      // KNOWN GAP (see template-cost-fixtures.ts): this call passes a literal
      // 'whole_sheet' rather than the forced cost mode, and omits the
      // converted cost_per_unit that every other
      // computeSheetCost call in this file passes (the other three all thread
      // opts?.forceCostMode ?? 'whole_sheet' as a 5th arg, and this file's own
      // slot loop above resolves a converted cost_per_unit it does not pass
      // here). The server threads its forced costMode into the equivalent
      // call (banquette-cost.helpers.ts:512). This client omission is
      // believed to be the wrong side — dormant only because
      // BanquetteBaseModal.tsx:396 hardcodes image_scale_ppi: 0, which
      // short-circuits this slot loop before it ever runs. Do not fix this
      // without first reading the fixture's KNOWN GAP note; fixing it changes
      // live project-estimate prices.
      const slotSqft = (realInches * lfQty * 12) / 144;
      const [numSheets, slotCost] = computeSheetCost(slotSqft, mat, baseWasteFactor, undefined, 'whole_sheet',
        { pieceQty: slotSqft > 0 ? 1 : 0, warnings: materialWarnings });
      finishCost += slotCost;

      slotDetails.push({
        slot_id: slotId,
        name: slot.name,
        calc_mode: slot.calc_mode ?? 'linear',
        real_inches: realInches,
        sqft: slotSqft,
        cost: slotCost,
        material_name: mat.name,
        num_sheets: numSheets > 0 ? numSheets : undefined,
        material_id: mat.id,
        material_category: (mat as any).category,
        material_subtype: (mat as any).fabric_subtype ?? null,
        waste_factor_used: baseWasteFactor,
        sheet_size: (mat.unit === 'sheet' || mat.unit === 'roll') ? `${mat.sheet_width ?? 48}x${mat.sheet_length ?? 96}` : undefined,
      });
    }
  }

  // Catalog traits for this design. Resolved once and reused everywhere below
  // that used to test design.type against string literals: the fabric
  // allowance chain here, the button count, and the tufting wire branch.
  const traits = resolveDesignTraits(design.type, opts?.designCatalog);

  // Per-material fabric packing + cost apportionment
  const fabricPackings: FabricPackingResult[] = [];
  for (const [matId, group] of piecesByMaterial) {
    const rollWidth = (group.mat as any).roll_width ?? 54;
    const patternDirection = coercePatternDirection((group.mat as any).pattern_direction);
    const result = packFabricPieces(
      group.pieces,
      { width_in: rollWidth, max_length_in: 1080 },
      patternDirection,
    );

    // design_pct: line-item override → per-design-type setting → material default → 0
    // Same chain the cushions use. A base shares their run/height axes, so the
    // channel and button counts resolve identically.
    let designPct: number;
    if ((config as any).fabric_design_pct != null) {
      designPct = (config as any).fabric_design_pct;
    } else {
      const channelsPerSection = resolveChannelCount(design, sectionWidthInches, profileHeight, traits);
      const totalButtons = resolveButtonCount(design, lfQty, traits);
      const buttonsPerSection = totalButtons / Math.max(1, sectionCount);
      const designTypePct = resolveDesignTypeAllowancePct(
        design.type ?? 'plain',
        upholsteryAllowances,
        channelsPerSection,
        buttonsPerSection,
        traits,
      );
      designPct = designTypePct != null ? designTypePct : ((group.mat as any).fabric_design_pct ?? 0);
    }
    const patternAllowancePct = (config as any).fabric_pattern_allowance_pct ?? 0;
    const matWasteFactor = group.mat.waste_factor ?? 0;

    const designBearingFraction = computeDesignBearingFraction(group.pieces);
    const effectiveDesignPct = designPct * designBearingFraction;

    const finalLengthIn =
      result.total_length_in *
      (1 + effectiveDesignPct + patternAllowancePct + matWasteFactor);
    const finalYards = finalLengthIn / 36;
    const cost = finalYards * (group.mat.cost_per_unit ?? 0);
    fabricCost += cost;

    const contributingSlotIds = Array.from(
      new Set(group.slotDetails.map((s) => s.slot_id).filter((id): id is string => !!id)),
    );

    fabricPackings.push({
      material_id: matId,
      material_name: group.mat.name,
      roll_width_in: rollWidth,
      pattern_direction: patternDirection,
      rolls: result.rolls,
      total_rolls: result.total_rolls,
      total_length_in: result.total_length_in,
      final_length_in: finalLengthIn,
      final_yards: finalYards,
      contributing_slot_ids: contributingSlotIds,
      design_pct: designPct,
      effective_design_pct: effectiveDesignPct,
      pattern_allowance_pct: patternAllowancePct,
      material_waste_factor: matWasteFactor,
      cost,
      warnings: result.warnings,
    });

    // Apportion cost back to slots by area share.
    const totalArea = group.pieces.reduce(
      (s, p) => s + p.width_in * p.length_in,
      0,
    );
    for (const slotDetail of group.slotDetails) {
      const slotArea = (slotDetail.fabric_pieces ?? []).reduce(
        (s, p) => s + p.width_in * p.length_in,
        0,
      );
      slotDetail.cost = totalArea > 0 ? cost * (slotArea / totalArea) : 0;
      if (result.warnings.length > 0) {
        slotDetail.note = [slotDetail.note, result.warnings.join('; ')]
          .filter(Boolean)
          .join(' | ');
      }
    }
  }

  // Applied wood finish. Net area only — no waste factor.
  const finishMap = new Map<string, FinishRate>(
    (opts?.finishes ?? []).map((f) => [
      f.id,
      { id: f.id, name: f.name, cost_per_sqft: Number(f.cost_per_sqft ?? 0) },
    ]),
  );
  const panelSqftEachForFinish = (profileDepth * profileHeight) / 144;
  const woodFinish = computeWoodFinish(
    collectFinishablePanels(framing, {
      sectionCount,
      ribCount,
      sidePanelSqftEach: panelSqftEachForFinish,
      ribSqftEach: panelSqftEachForFinish,
      railRunLengthIn: sectionCount > 0 ? (lfQty * 12) / sectionCount : 0,
    }),
    (framing as any).default_finish_id,
    finishMap,
  );

  // ── Foam ──
  // Each layer runs the full LF length at its own width, like a rail. Curved
  // sections carry extra waste blended by their share of the item.
  const foamWaste = ((config as any).foam_waste_factor ?? 0.10)
    + Math.max(0, (config as any).foam_curved_waste_factor ?? 0) * curvedFraction;
  const runLengthIn = lfQty * 12;
  const foamByMat = new Map<
    string,
    /** totalQty counts discrete pieces; only `each` pricing reads it. */
    { mat: MaterialLibrary; totalSqft: number; totalQty: number }
  >();
  const foamLayerRows: Array<{ name: string; width: number; sqft: number }> = [];
  const foamMaterials: BanquetteMaterialDetail[] = [];
  let foamCost = 0;
  let foamSqft = 0;

  // Mirrors banquette-cost.helpers.ts: resolved against the component's live
  // dimensions so this preview and the saved server estimate agree. A null
  // length means Auto — the full run.
  const resolvedFoam = resolveFoamLayers(
    ((config as any).foam_layers ?? []) as Array<any>,
    componentDims(config as any),
  );

  for (const layer of resolvedFoam) {
    if (!layer.material_id) continue;
    const mat = matMap.get(layer.material_id);
    if (!mat) continue;
    const layerSqft = (layer.length * layer.width) / 144.0;
    if (layerSqft <= 0) continue;

    foamLayerRows.push({ name: layer.name ?? '', width: layer.width, sqft: layerSqft });
    foamSqft += layerSqft;

    const group = foamByMat.get(layer.material_id) ?? { mat, totalSqft: 0, totalQty: 0 };
    group.totalSqft += layerSqft;
    group.totalQty += 1;
    foamByMat.set(layer.material_id, group);
  }

  for (const [matId, group] of foamByMat) {
    const [numSheets, cost, sheetSqft] = computeSheetCost(
      group.totalSqft,
      group.mat,
      foamWaste,
      undefined,
      opts?.forceCostMode ?? 'whole_sheet',
      { pieceQty: group.totalQty, warnings: materialWarnings },
    );
    foamCost += cost;
    foamMaterials.push({
      material_id: matId,
      material_name: group.mat.name,
      total_sqft: group.totalSqft,
      sqft_with_waste: group.totalSqft * (1 + foamWaste),
      cost,
      num_sheets: sheetSqft > 0 ? numSheets : undefined,
      sheet_size: sheetSqft > 0
        ? `${(group.mat as any).sheet_width ?? 48}x${(group.mat as any).sheet_length ?? 96}`
        : undefined,
    });
  }

  // ── Dacron ──
  // Wraps the front face: run length x profile height.
  const dacronWaste = ((config as any).dacron_waste_factor ?? 0.10)
    + Math.max(0, (config as any).dacron_curved_waste_factor ?? 0) * curvedFraction;
  const dacronMat = (config as any).dacron_material_id
    ? matMap.get((config as any).dacron_material_id)
    : null;
  let dacronCost = 0;
  let dacronSqft = 0;
  if (dacronMat && lfQty > 0 && profileHeight > 0) {
    dacronSqft = (runLengthIn * profileHeight) / 144.0;
    const [, cost] = computeSheetCost(
      dacronSqft,
      dacronMat,
      dacronWaste,
      undefined,
      opts?.forceCostMode ?? 'whole_sheet',
      { pieceQty: 1, warnings: materialWarnings },
    );
    dacronCost = cost;
  }

  // ── Hardware ──
  const hw = hardwareMap ?? new Map();
  const springs = (config as any).springs ?? {};
  const hardwareItems: BanquetteCostPreviewResult['hardware_items'] = [];
  let hardwareCost = 0;
  const hardwareWarnings: string[] = [];

  // Buttons. Raw cost_per_unit, no conversion — matches BanquetteCostService,
  // which converts nothing. See spec 2026-09-02 D3.
  const buttonCount = resolveButtonCount(design, lfQty, traits);
  const button = resolveButtonHardware(
    buttonCount,
    design.button_hardware_id,
    defaultButtonHardwareId,
    (id) => hw.get(id),
  );
  if (button.warning) hardwareWarnings.push(button.warning);
  if (button.name) {
    hardwareCost += button.cost;
    hardwareItems.push({
      name: button.name,
      hardware_id: button.hardware_id!,
      qty: buttonCount,
      cost_per_unit: button.cost_per_unit,
      cost: button.cost,
      unit: 'each',
    });
  }

  // Tufting wire. Which designs carry it, and which axis it runs along, are
  // catalog traits now — a design an admin marks as tufted earns wire here with
  // no code change.
  const channelCountPerSection = resolveChannelCount(
    design, sectionWidthInches, profileHeight, traits,
  );
  const channelWarning = resolveChannelWidthWarning(design, traits);
  if (channelWarning) hardwareWarnings.push(channelWarning);
  const wireLf = resolveWireLinearFeet(
    channelCountPerSection, traits, sectionWidthInches, profileHeight, sectionCount,
  );
  const wire = resolveWireHardware(
    wireLf,
    design.wire_hardware_id,
    defaultWireHardwareId,
    (id) => hw.get(id),
  );
  if (wire.warning) hardwareWarnings.push(wire.warning);
  hardwareWarnings.push(...materialWarnings);
  if (wire.name) {
    hardwareCost += wire.cost;
    hardwareItems.push({
      name: wire.name,
      hardware_id: wire.hardware_id!,
      qty: wireLf,
      cost_per_unit: wire.cost_per_unit,
      cost: wire.cost,
      unit: 'lin ft',
    });
  }

  // No-sag springs (gated on springs_enabled — item-level toggle)
  const springsEnabled = !!springs.springs_enabled;
  const springCount = springsEnabled ? resolveSpringCount(springs, lfQty) : 0;
  if (springsEnabled && springCount > 0 && springs.spring_hardware_id) {
    const item = hw.get(springs.spring_hardware_id);
    if (item) {
      const cost = springCount * item.cost_per_unit;
      hardwareCost += cost;
      hardwareItems.push({
        name: item.name, hardware_id: springs.spring_hardware_id,
        qty: springCount, cost_per_unit: item.cost_per_unit, cost, unit: 'each',
      });
    }
  }

  // Spring clips: two per spring end, plus one per spring per connecting wire.
  const connectingWireCount = springs.connecting_wire_count ?? 1;
  const clipCount = springCount * 2 + springCount * connectingWireCount;
  if (springsEnabled && clipCount > 0 && springs.clip_hardware_id) {
    const item = hw.get(springs.clip_hardware_id);
    if (item) {
      const cost = clipCount * item.cost_per_unit;
      hardwareCost += cost;
      hardwareItems.push({
        name: item.name, hardware_id: springs.clip_hardware_id,
        qty: clipCount, cost_per_unit: item.cost_per_unit, cost, unit: 'each',
      });
    }
  }

  // Connecting wire
  const connectingWireLf = springsEnabled ? connectingWireCount * lfQty : 0;
  if (springsEnabled && connectingWireLf > 0 && springs.connecting_wire_hardware_id) {
    const item = hw.get(springs.connecting_wire_hardware_id);
    if (item) {
      const cost = connectingWireLf * item.cost_per_unit;
      hardwareCost += cost;
      hardwareItems.push({
        name: item.name, hardware_id: springs.connecting_wire_hardware_id,
        qty: connectingWireLf, cost_per_unit: item.cost_per_unit, cost, unit: 'lin ft',
      });
    }
  }

  const totalCost =
    framingCost + foamCost + dacronCost + finishCost + fabricCost + hardwareCost
    + woodFinish.cost;

  return {
    framing_cost: framingCost,
    finish_cost: finishCost,
    fabric_cost: fabricCost,
    foam_cost: foamCost,
    dacron_cost: dacronCost,
    hardware_cost: hardwareCost,
    hardware_warnings: hardwareWarnings,
    foam_sqft: foamSqft,
    dacron_sqft: dacronSqft,
    foam_materials: foamMaterials,
    foam_layers: foamLayerRows,
    spring_count: springCount,
    clip_count: clipCount,
    connecting_wire_lf: connectingWireLf,
    button_count: buttonCount,
    wire_lf: wireLf,
    hardware_items: hardwareItems,
    wood_finish_cost: woodFinish.cost,
    wood_finish_details: woodFinish.rows,
    total_cost: totalCost,
    total_cost_excluding_fabric: totalCost - fabricCost,
    rate_per_lf: lfQty > 0 ? totalCost / lfQty : 0,
    framing_materials: framingMaterials,
    slot_details: slotDetails,
    rib_count: ribCount,
    fabric_packings: fabricPackings,
    nesting_parts: nestingParts,
  };
}
